import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LuEye, LuEyeOff, LuGripVertical, LuSettings2 } from 'react-icons/lu';

import { InsightTag } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';

const TAG_LABELS: Record<InsightTag, string> = {
    [InsightTag.E1]: 'Birthday',
    [InsightTag.E2]: 'Age Milestone',
    [InsightTag.E3]: 'Outreach Opportunity',
    [InsightTag.E4]: 'New Child',
    [InsightTag.N1]: 'Stale Opportunity',
    [InsightTag.N2]: 'Opportunity Needs Attention',
    [InsightTag.N3]: 'Outside Asset Detected',
    [InsightTag.N4]: 'Referral Opportunity',
    [InsightTag.N5]: 'New Prospect Mentioned',
    [InsightTag.N6]: 'Prospect Pitch',
    [InsightTag.R1]: 'Week in Review',
    [InsightTag.R2]: 'Client Asked a Question',
    [InsightTag.R3]: 'Awaiting Client Reply',
};

interface SortableTagItemProps {
    tag: InsightTag;
    isHidden: boolean;
    onToggle: () => void;
}

const SortableTagItem = ({ tag, isHidden, onToggle }: SortableTagItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tag });

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
            className="border-border mb-1 flex items-center justify-between rounded-md border bg-white px-3 py-2"
        >
            <div className="flex items-center gap-3">
                <button
                    className="cursor-grab touch-none text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                    {...attributes}
                    {...listeners}
                >
                    <LuGripVertical className="h-4 w-4" />
                </button>
                <span className="text-sm">{TAG_LABELS[tag]}</span>
            </div>
            <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={onToggle}
                aria-label="toggle insight type visibility"
            >
                {isHidden ? <LuEyeOff className="h-4 w-4 text-red-400" /> : <LuEye className="h-4 w-4" />}
            </Button>
        </div>
    );
};

interface InsightSettingsProps {
    tagOrder: InsightTag[];
    hiddenTags: InsightTag[];
    onTagOrderChange: (order: InsightTag[]) => void;
    onToggleTag: (tag: InsightTag) => void;
    onReset: () => void;
}

export const InsightSettings = ({
    tagOrder,
    hiddenTags,
    onTagOrderChange,
    onToggleTag,
    onReset,
}: InsightSettingsProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = tagOrder.indexOf(active.id as InsightTag);
            const newIndex = tagOrder.indexOf(over.id as InsightTag);
            onTagOrderChange(arrayMove(tagOrder, oldIndex, newIndex));
        }
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="ghost2" size="sm" className="h-9 w-9 p-0">
                    <LuSettings2 className="h-4 w-4" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Insights settings</DialogTitle>
                </DialogHeader>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={tagOrder}>
                        <div className="space-y-1">
                            {tagOrder.map((tag) => (
                                <SortableTagItem
                                    key={tag}
                                    tag={tag}
                                    isHidden={hiddenTags.includes(tag)}
                                    onToggle={() => onToggleTag(tag)}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
                <DialogFooter>
                    <Button variant="outline" onClick={onReset}>
                        Reset
                    </Button>
                    <DialogClose asChild>
                        <Button>Close</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
